/**
 * Roster Tab - Clinicians and clients assigned to this center
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { ArrowRightLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import locationService, { RosterClinician, RosterClient } from '../../services/api/locationService';
import { ClientTransferDrawer } from './ClientTransferDrawer';
import { ClinicianTransferDrawer } from './ClinicianTransferDrawer';

interface RosterTabProps {
  locationId: string;
}

// ==================== Styled Components ====================

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const TableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const ThActions = styled(Th)`
  width: 120px;
`;

const Tr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;
`;

const RoleBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[10]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const TransferButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.primary.ocean};
    border-color: ${({ theme }) => theme.colors.primary.ocean};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// ==================== Sub-components ====================

interface ClinicianTableProps {
  locationId: string;
}

interface TransferClinicianTarget {
  memberId: string;
  clinicianName: string;
}

const ClinicianTable = ({ locationId }: ClinicianTableProps) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [transferTarget, setTransferTarget] = useState<TransferClinicianTarget | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['location-roster-clinician', locationId, page],
    queryFn: () =>
      locationService.getLocationRoster(locationId, { type: 'clinician', page, limit: 10 }),
  });

  const clinicians = (data?.data ?? []) as RosterClinician[];
  const meta = data?.meta;

  const handleTransferred = () => {
    queryClient.invalidateQueries({ queryKey: ['location-roster-clinician', locationId] });
    queryClient.invalidateQueries({ queryKey: ['location-roster-client', locationId] });
  };

  return (
    <>
      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Roles</Th>
              <Th>Supervisor</Th>
              <ThActions>Transfer</ThActions>
            </tr>
          </Thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  <Td>
                    <SkeletonBar $width="140px" />
                  </Td>
                  <Td>
                    <SkeletonBar $width="180px" />
                  </Td>
                  <Td>
                    <SkeletonBar $width="80px" />
                  </Td>
                  <Td>
                    <SkeletonBar $width="120px" />
                  </Td>
                  <Td />
                </tr>
              ))
            ) : clinicians.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState>No clinicians assigned to this center.</EmptyState>
                </td>
              </tr>
            ) : (
              clinicians.map((c) => (
                <Tr key={c.id}>
                  <Td>
                    {c.firstName} {c.lastName}
                  </Td>
                  <Td>
                    <SecondaryText>{c.email}</SecondaryText>
                  </Td>
                  <Td>
                    <RoleBadges>
                      {(c.roles ?? []).map((r) => (
                        <Badge key={r.id} roleName={r.name}>
                          {r.name}
                        </Badge>
                      ))}
                    </RoleBadges>
                  </Td>
                  <Td>
                    <SecondaryText>{c.supervisorName ?? '—'}</SecondaryText>
                  </Td>
                  <Td>
                    <TransferButton
                      title="Transfer clinician"
                      onClick={() =>
                        setTransferTarget({
                          memberId: c.id,
                          clinicianName: `${c.firstName} ${c.lastName}`,
                        })
                      }
                    >
                      <ArrowRightLeft />
                      Transfer
                    </TransferButton>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
        {meta && meta.totalPages > 1 && (
          <Pagination>
            <PaginationInfo>
              Page {meta.page} of {meta.totalPages}
            </PaginationInfo>
            <PaginationControls>
              <PageButton
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                aria-label="Previous"
              >
                <ChevronLeft />
              </PageButton>
              <PageButton
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= meta.totalPages}
                aria-label="Next"
              >
                <ChevronRight />
              </PageButton>
            </PaginationControls>
          </Pagination>
        )}
      </TableWrapper>

      {transferTarget && (
        <ClinicianTransferDrawer
          centerId={locationId}
          memberId={transferTarget.memberId}
          clinicianName={transferTarget.clinicianName}
          onClose={() => setTransferTarget(null)}
          onTransferred={handleTransferred}
        />
      )}
    </>
  );
};

interface ClientTableProps {
  locationId: string;
}

interface TransferClientTarget {
  clientId: string;
  clientName: string;
}

const ClientTable = ({ locationId }: ClientTableProps) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [transferTarget, setTransferTarget] = useState<TransferClientTarget | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['location-roster-client', locationId, page],
    queryFn: () =>
      locationService.getLocationRoster(locationId, { type: 'client', page, limit: 10 }),
  });

  const clients = (data?.data ?? []) as RosterClient[];
  const meta = data?.meta;

  const handleTransferred = () => {
    queryClient.invalidateQueries({ queryKey: ['location-roster-client', locationId] });
    queryClient.invalidateQueries({ queryKey: ['location-roster-clinician', locationId] });
  };

  return (
    <>
      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Name</Th>
              <Th>Age</Th>
              <Th>Assigned To</Th>
              <ThActions>Transfer</ThActions>
            </tr>
          </Thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  <Td>
                    <SkeletonBar $width="140px" />
                  </Td>
                  <Td>
                    <SkeletonBar $width="40px" />
                  </Td>
                  <Td>
                    <SkeletonBar $width="140px" />
                  </Td>
                  <Td />
                </tr>
              ))
            ) : clients.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <EmptyState>No clients assigned to this center.</EmptyState>
                </td>
              </tr>
            ) : (
              clients.map((c) => (
                <Tr key={c.id}>
                  <Td>
                    {c.firstName} {c.lastName}
                  </Td>
                  <Td>
                    <SecondaryText>{c.age != null ? c.age : '—'}</SecondaryText>
                  </Td>
                  <Td>
                    <SecondaryText>{c.assigneeName ?? '—'}</SecondaryText>
                  </Td>
                  <Td>
                    <TransferButton
                      title="Transfer client"
                      onClick={() =>
                        setTransferTarget({
                          clientId: c.id,
                          clientName: `${c.firstName} ${c.lastName}`,
                        })
                      }
                    >
                      <ArrowRightLeft />
                      Transfer
                    </TransferButton>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
        {meta && meta.totalPages > 1 && (
          <Pagination>
            <PaginationInfo>
              Page {meta.page} of {meta.totalPages}
            </PaginationInfo>
            <PaginationControls>
              <PageButton
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                aria-label="Previous"
              >
                <ChevronLeft />
              </PageButton>
              <PageButton
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= meta.totalPages}
                aria-label="Next"
              >
                <ChevronRight />
              </PageButton>
            </PaginationControls>
          </Pagination>
        )}
      </TableWrapper>

      {transferTarget && (
        <ClientTransferDrawer
          centerId={locationId}
          clientId={transferTarget.clientId}
          clientName={transferTarget.clientName}
          onClose={() => setTransferTarget(null)}
          onTransferred={handleTransferred}
        />
      )}
    </>
  );
};

// ==================== Main Export ====================

export const RosterTab = ({ locationId }: RosterTabProps) => (
  <Sections>
    <div>
      <SectionTitle>Clinicians</SectionTitle>
      <ClinicianTable locationId={locationId} />
    </div>
    <div>
      <SectionTitle>Clients</SectionTitle>
      <ClientTable locationId={locationId} />
    </div>
  </Sections>
);
